import React, { useEffect, useState, useCallback, useMemo } from 'react';
import SimpleTableViewer from '../SimpleTable';
import SpendCardViewer from '../Spend/SpendCard';
import { checkConditions } from '../../TrackerViewer/utils';
import useStyles from './style';

function PriceAdherenceDetails(props) {
    const classes = useStyles();
    const { priceAdherenceDetails, blockState = {} } = props;

    /** Table columns: same rules as StepLevelBlocks / CardInfo — show only when `conditions` match blockState */
    const visibleSpendColumns = useMemo(() => {
        const els = priceAdherenceDetails?.spendInformation?.dataElement;
        if (!Array.isArray(els)) return [];
        return els.filter((item) => checkConditions(item?.conditions, blockState));
    }, [priceAdherenceDetails?.spendInformation?.dataElement, blockState]);
    const [tableData, setTableData] = useState({});
    const [cardData, setCardData] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sort, setSort] = useState({});
    const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);

    const handlePageChange = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleRowsPerPageChange = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    const handleSortChange = useCallback((newSort) => {
        setSort(newSort);
    }, []);

    const handleSingleRowDownload = useCallback((e, rowData) => {
        const rowId = rowData?.id || rowData?._id;
        if (!rowId) {
            console.warn('No document ID found in row data');
            return;
        }
        const fieldList = visibleSpendColumns.length
            ? visibleSpendColumns.map((i) => i.value).join(',')
            : (priceAdherenceDetails?.spendInformation?.dataElement || []).map((i) => i.value).join(',');
        let payload = {
            dataModelId: priceAdherenceDetails.dataModelId,
            appid: props.match.params.appid,
            fields: fieldList,
            downloadType: 'excel',
            ids: [rowId],
            dataUniqueId: props.data.chartUniqueId + '_' + 'single_row' + '_download'
        }
        props.downloadSheet(payload);
    }, [visibleSpendColumns, priceAdherenceDetails, props.match?.params?.appid, props.data?.chartUniqueId, props.downloadSheet]);

    const fetchData = useCallback(() => {
        if (!props.getSpendDetails || !priceAdherenceDetails?.dataModelId || !props.match?.params?.id) return;
        props.getSpendDetails({
            appId: props.match.params.appid,
            dataModelId: priceAdherenceDetails.dataModelId,
            mainDataModelId: priceAdherenceDetails.mainDataModelId,
            documentId: props.match.params.id,
            dataUniqueId: props.data?.chartUniqueId + '_priceAdherenceDetails_tableData',
            field: priceAdherenceDetails?.field || 'documentId',
            ...(priceAdherenceDetails?.combineFieldsIntoSingleField && { combineFieldsIntoSingleField: true }),
            sort: sort,
            payload: {
                dataModelId: priceAdherenceDetails.dataModelId,
                mainDataModelId: priceAdherenceDetails.mainDataModelId,
                documentId: props.match.params.id,
                field: priceAdherenceDetails?.field || 'documentId',
                ...(priceAdherenceDetails?.combineFieldsIntoSingleField && { combineFieldsIntoSingleField: true }),
                sort: sort,
                page: page,
                rowsPerPage: rowsPerPage
            }
        });
    }, [priceAdherenceDetails, props.match?.params?.id, sort, page, rowsPerPage]);

    useEffect(() => {
        if (props.activeTabMain === 'priceAdherenceDetails') {
            fetchData();
        }
    }, [props.activeTabMain, fetchData]);

    useEffect(() => {
        const dataKey = props.data?.chartUniqueId + '_priceAdherenceDetails_tableData';
        if (dataKey && props.formData?.document?.[dataKey]) {
            const responseData = props.formData.document[dataKey] || {};
            setTableData(responseData);
            Object.keys(responseData).forEach(key => {
                const dataElement = priceAdherenceDetails?.uploadCard?.dataElement?.find(item => item.value === key);
                if (dataElement) {
                    setCardData(prev => ({
                        ...prev,
                        [key]: responseData[dataElement.value] || ''
                    }));
                }
            });
        }
    }, [props.formData?.document?.[props.data?.chartUniqueId + '_priceAdherenceDetails_tableData']]);

    if (!priceAdherenceDetails) return null;

    return (
        <div className={classes.root}>
            {priceAdherenceDetails?.uploadCard?.dataElement && (
                <SpendCardViewer
                    {...props}
                    data={priceAdherenceDetails?.uploadCard?.dataElement}
                    blockState={cardData}
                    isLoading={props.formData?.[props.data?.chartUniqueId + '_priceAdherenceDetails_tableData_loading']}
                />
            )}
            {priceAdherenceDetails?.isCustomType?.enable && (
                <div className={classes.tableHost}>
                    <SimpleTableViewer
                        {...props}
                        title={priceAdherenceDetails?.title}
                        dataObj={tableData?.data}
                        selectedDataElement={[]}
                        dataModel={priceAdherenceDetails?.dataModel}
                        selectedTableDataElement={visibleSpendColumns}
                        isActionEnabled={priceAdherenceDetails?.spendInformation?.actions?.enable}
                        isObjectTable={false}
                        pagination={true}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        totalRecords={tableData?.totalRecords}
                        onDownload={handleSingleRowDownload}
                        isDownloadButton={true}
                        isCheckbox={false}
                        sort={sort}
                        onSortChange={handleSortChange}
                        onCheckboxChange={(updatedSelection) => setSelectedDocumentIds(updatedSelection)}
                        selectedRows={selectedDocumentIds}
                    />
                </div>
            )}
        </div>
    );
}

export default PriceAdherenceDetails;